import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-6 py-12">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.25, 1, 0.5, 1] }}
        className="w-full max-w-md"
      >
        <Link
          to="/"
          data-testid="back-home-link"
          className="inline-flex items-center gap-2 eyebrow text-charcoal/60 hover:text-maroon transition-colors mb-10"
        >
          <ArrowLeft size={14} /> Back to site
        </Link>

        <div className="text-center mb-10">
          <h1 className="font-display text-charcoal text-4xl leading-none">Emmy by Design</h1>
          <p className="eyebrow text-burgundy mt-3">Studio Login</p>
        </div>

        <form data-testid="login-form" onSubmit={submit} className="space-y-7">
          <div>
            <label className="eyebrow text-charcoal/70">Email</label>
            <input
              data-testid="login-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-transparent border-b border-charcoal/40 focus:border-maroon outline-none py-3 font-body text-charcoal transition-colors mt-2"
            />
          </div>
          <div>
            <label className="eyebrow text-charcoal/70">Password</label>
            <input
              data-testid="login-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-transparent border-b border-charcoal/40 focus:border-maroon outline-none py-3 font-body text-charcoal transition-colors mt-2"
            />
          </div>

          {error && (
            <p data-testid="login-error" className="font-body text-maroon text-sm">
              {error}
            </p>
          )}

          <button
            data-testid="login-submit-button"
            type="submit"
            disabled={loading}
            className="w-full font-body bg-maroon text-cream py-4 text-sm tracking-wide hover:bg-charcoal transition-colors duration-500 disabled:opacity-60"
          >
            {loading ? "Signing in…" : "Enter Studio"}
          </button>
        </form>
      </motion.div>
    </div>
  );
}
